import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import CTASection from '../components/CTASection';
import { 
  ArrowRight,
  ArrowsLeftRight,
  Bell,
  CheckCircle,
  Clock,
  Users,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { openAuthModal } = useAuth();
  const [email, setEmail] = React.useState('');
  const [subscribed, setSubscribed] = React.useState(false);

  const handleNotify = (e) => {
    e.preventDefault();
    if (email) {
      // In a real implementation, this would call an API
      setSubscribed(true);
    }
  };

  const features = [
    {
      icon: ArrowsLeftRight,
      title: 'Peer-to-peer trading',
      description: 'Buy and sell your positions directly with other investors.',
    },
    {
      icon: Clock,
      title: 'Liquidity on your terms',
      description: 'Exit investments before the project lifecycle ends.',
    },
    {
      icon: Users,
      title: 'Transparent pricing',
      description: 'Market-driven pricing based on supply and demand.',
    },
  ];

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="marketplace-page">
      <Header />

      {/* Hero Section with background image */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background Image - Group of people */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/q5jkhg1o_ChatGPT%20Image%20Sep%207%2C%202026%20at%2004_22_52%20PM.png")'
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-slate-900/70" />
        
        <div className="container mx-auto px-6 md:px-12 lg:px-20 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            {/* Coming Soon Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm font-medium mb-4"
            >
              <Clock size={16} weight="bold" />
              Coming Soon
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4 text-white">
              Flippex Marketplace
            </h1>
            <p className="text-lg md:text-xl text-white/60">
              Trade your real estate positions with other investors. Liquidity when you need it.
            </p>
          </div>

          {/* Notify Form */}
          <div className="max-w-xl mx-auto mt-10">
            {subscribed ? (
              <div className="flex items-center justify-center gap-3 bg-primary/20 text-primary px-6 py-4 rounded-full">
                <CheckCircle size={24} weight="fill" />
                <span className="font-medium">You&apos;re on the list. We&apos;ll notify you when we launch.</span>
              </div>
            ) : (
              <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 items-stretch">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-6 h-14 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  required
                />
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 h-14 font-medium transition-all duration-300 hover:scale-[1.02]"
                >
                  <Bell size={18} className="mr-2" />
                  Notify me
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-4">
              What to expect
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight">
              A new way to manage your portfolio.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-[rgb(var(--background))] rounded-3xl p-8 border border-black/[0.04]"
                >
                  <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                    <Icon className="text-primary" size={32} weight="duotone" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-foreground-muted leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Will Work Section */}
      <section className="py-24 md:py-32 bg-[rgb(var(--background))]">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-4">
                How it will work
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight mb-6">
                Sell when you want.
                <br />
                <span className="text-slate-400">Buy what others offer.</span>
              </h2>
              <p className="text-lg text-foreground-muted mb-8 leading-relaxed">
                List your positions at a price you set. Other investors can browse and purchase 
                available positions. Ownership transfers instantly upon completion.
              </p>
              <ul className="space-y-4">
                {[
                  'List positions from your portfolio',
                  'Set your own asking price',
                  'Browse available listings',
                  'Secure peer-to-peer transfers',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="text-primary flex-shrink-0" size={20} weight="fill" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-center" style={{ perspective: '1000px' }}>
              <motion.img 
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/0j4udx7s_687FB619-526B-4574-B8AD-F2E1F4461CB4.png"
                alt="Trade"
                className="w-full max-w-[300px] h-auto object-contain"
                animate={{ 
                  rotateY: [-10, 10, -10]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />

      <Footer />
    </div>
  );
};

export default MarketplacePage;
